import React, {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { config } from "../config";
import { useRefreshStatus, useSecureToken, useStatus } from "./StatusProvider";
import { type ID, type TimeZone } from "../../types";
import { api } from "../helpers/api";
import { isEqual } from "lodash";
import { errorReporting } from "../helpers/errorReporting";
import { type AxiosError } from "axios";
import { useDeviceId } from "./DeviceProvider";
import { useTranslation } from "react-i18next";
import { LoadingScreen } from "../screens/LoadingScreen";
import { ThemeResponse, useSetTheme } from "./ThemeProvider";
import { initLocalisation, LocaleName } from "../helpers/localise";

export type PlaylistItem = {
  configId: ID;
  orderId: number;
  mode: "looped" | "timed";
  numberLoops: number;
  duration: number;
};

type PlaylistResponse =
  | {
      status: "not-found";
    }
  | {
      status: "active";
      deviceName: string;
      deviceTimeZone?: TimeZone;
      skipInteractionScreen: boolean;
      announcements: string[];
      theme: ThemeResponse;
      locale?: LocaleName;
      playlist: Array<PlaylistItem>;
    };

export type PlaylistContext =
  | PlaylistResponse
  | { status: "error"; message: string };

const Context = createContext<PlaylistContext>(null!);

export const PlaylistProvider = (props: PropsWithChildren) => {
  const { t } = useTranslation("PlaylistProvider");
  const status = useStatus();
  const secureToken = useSecureToken({ strict: false });
  const refreshStatus = useRefreshStatus();
  const deviceId = useDeviceId();
  const setTheme = useSetTheme();

  const [networkError, setNetworkError] = useState<string>();
  const [data, setData] = useState<PlaylistContext>();
  const active = status.status === "active" || status.status === "hard_coded";

  useEffect(() => {
    if (!active) return;

    const refresh = () => {
      const req =
        status.status === "hard_coded"
          ? status.type === "device"
            ? api(status.region).get<PlaylistResponse>(
                `/screens/virtual_device/${status.urlCode}/playlist`,
              )
            : status.type === "playlist"
              ? api(status.region).get<PlaylistResponse>(
                  `/screens/${status.urlCode}/hardcoded_playlist`,
                )
              : api(status.region).get<PlaylistResponse>(
                  `/screens/${status.urlCode}/hardcoded_config`,
                )
          : api(status.region, secureToken).get<PlaylistResponse>(
              `/screens/${deviceId}/playlist`,
            );

      req
        .then((r) => {
          if (!isEqual(r.data, data)) {
            setData(r.data);
            // if it was the first request
            if (data == null && r.data.status === "active") {
              setTheme(r.data.theme);
              initLocalisation(r.data.locale);
            }
          }
        })
        .catch((err: AxiosError) => {
          errorReporting.sendError(err);

          // don't want to retry on network error, as it would trigger an infinite loop
          if (err.code === "ERR_NETWORK" || err.code === "ERR_BAD_RESPONSE") {
            // only display network error if there is no data to fallback on
            if (data == null) {
              setNetworkError(`${err.code}: ${err.message}`);
            }
            return;
          }

          refreshStatus();
        });
    };

    if (data == null) {
      refresh();
      return;
    }

    const interval = setInterval(refresh, config.playlistPollingRate);
    return () => clearInterval(interval);
  }, [data, deviceId, secureToken, status, setTheme, refreshStatus, active]);

  if (active && data == null && networkError == null) {
    return <LoadingScreen message={t("Loading playlist")} />;
  }

  return (
    <Context.Provider
      {...props}
      value={
        networkError != null
          ? { status: "error", message: networkError }
          : (data ?? { status: "not-found" })
      }
    />
  );
};

export const usePlaylist = () => {
  const ctx = useContext(Context);
  if (ctx == null) {
    throw new Error("usePlaylist: context not ready");
  }
  return ctx;
};

export const useActivePlaylist = () => {
  const playlist = usePlaylist();
  if (playlist.status !== "active") {
    throw new Error("useActivePlaylist: playlist not active");
  }
  return playlist;
};

export const useAnnouncements = () => {
  const ctx = useContext(Context);
  if (ctx == null) {
    throw new Error("useAnnouncements: context not ready");
  }

  return ctx?.status === "active" ? (ctx?.announcements ?? []) : [];
};

export const useSettings = (args?: { strict?: boolean }) => {
  const ctx = useContext(Context);
  const activeCtx = ctx?.status === "active" ? ctx : null;
  const { t } = useTranslation("PlaylistProvider");

  if (args?.strict !== false && activeCtx == null) {
    throw new Error("useSettings: context not ready");
  }

  return {
    deviceName: activeCtx?.deviceName ?? t("Loading..."),
    deviceTimeZone: activeCtx?.deviceTimeZone,
  };
};
